import {
    adminListSoftware,
    adminCreateSoftware,
    adminUpdateSoftware,
    adminUploadLogo,
    adminSetInstitutionAccess,
    adminListGrants,
    adminCreateGrant,
    adminRevokeGrant,
    adminListOrgUnits,
    adminSyncDirectory,
    adminSyncState,
    adminSearchUsers,
    adminAccessPreview,
    adminAuditLogs
} from './utils/api.js';
import { Modal } from '@magickainteractive/modals';
import { showError } from './utils/error.js';
import { showToast } from './utils/toast.js';
import { showConfirm } from './utils/confirm.js';
import {
    animateElement,
    crossFade,
    fadeIn,
    fadeOut,
    staggerChildren
} from './utils/animate.js';
import { tableLoadingRow } from './utils/loading.js';

let selectedSoftwareId = null;
let softwareCache = [];
let orgUnitsCache = [];

function $(id) {
    return document.getElementById(id);
}

function visiblePage() {
    if (!$('home').classList.contains('is-hidden')) {
        return $('home');
    }
    if (!$('settings').classList.contains('is-hidden')) {
        return $('settings');
    }
    if (!$('admin').classList.contains('is-hidden')) {
        return $('admin');
    }
    return null;
}

function animateAdminShell() {
    const admin = $('admin');
    animateElement(admin.querySelector('.settings-header'), 0);
    staggerChildren(admin, '[data-admin-tab]', 45, 40);
    const panel = admin.querySelector('[data-admin-panel]:not(.is-hidden)');
    if (panel) {
        animateElement(panel, 100);
    }
}

export function isPlatformAdmin(profile) {
    return !!(profile && (profile.global_admin || profile.institution_admin));
}

export async function showAdminConsole() {
    const admin = $('admin');
    const from = visiblePage();

    if (from && from !== admin) {
        await crossFade(from, admin);
    } else {
        await fadeIn(admin);
    }

    animateAdminShell();

    await Promise.all([
        loadAdminSoftware(),
        refreshSyncState(),
        filterOrgUnits('')
    ]);
}

export async function hideAdminConsole() {
    const admin = $('admin');
    if (admin.classList.contains('is-hidden')) {
        return;
    }
    await fadeOut(admin);
}

export function configureAdminUi() {
    document.querySelectorAll('[data-admin-tab]').forEach((btn) => {
        btn.addEventListener('click', async () => {
            const tab = btn.getAttribute('data-admin-tab');
            const next = document.querySelector(`[data-admin-panel="${tab}"]`);
            if (!next) {
                return;
            }

            const current = document.querySelector('#admin [data-admin-panel]:not(.is-hidden)');
            if (current === next) {
                return;
            }

            document.querySelectorAll('[data-admin-tab]').forEach((b) => b.classList.remove('is-active'));
            btn.classList.add('is-active');

            // Hide every panel first so a cancelled mid-animation switch can't leave none visible
            document.querySelectorAll('#admin [data-admin-panel]').forEach((panel) => {
                panel.classList.remove('view-enter', 'view-exit', 'stagger-enter');
                panel.style.animationDelay = '';
                if (panel !== next) {
                    panel.classList.add('is-hidden');
                }
            });

            await fadeIn(next);
            animateElement(next, 0);

            if (tab === 'access') {
                populateAccessSoftwareSelect();
                filterOrgUnits($('admin-ou-search')?.value || '');
                if (selectedSoftwareId) {
                    loadGrants();
                }
            }
            if (tab === 'audit') {
                loadAudit();
            }
        });
    });

    $('admin-software-refresh')?.addEventListener('click', () => loadAdminSoftware());
    $('admin-create-software-btn')?.addEventListener('click', () => createSoftware());
    $('admin-grants-software')?.addEventListener('change', (e) => {
        selectedSoftwareId = e.target.value || null;
        loadGrants();
    });
    $('admin-add-grant-btn')?.addEventListener('click', () => addGrant());
    $('admin-ou-search')?.addEventListener('input', (e) => filterOrgUnits(e.target.value));
    $('admin-sync-btn')?.addEventListener('click', () => runSync());
    $('admin-preview-btn')?.addEventListener('click', () => runPreview());
    $('admin-user-email')?.addEventListener('input', debounce(async (e) => {
        const q = e.target.value.trim();
        if (q.length < 2) {
            return;
        }
        try {
            const res = await adminSearchUsers(q);
            const list = $('admin-user-suggestions');
            list.innerHTML = '';
            (res.data || []).forEach((u) => {
                const opt = document.createElement('option');
                opt.value = u.email || u.id;
                list.appendChild(opt);
            });
        } catch (_) {
            // ignore
        }
    }, 250));

    document.querySelectorAll('input[name="grant-principal"]').forEach((radio) => {
        radio.addEventListener('change', () => {
            const isUser = $('grant-principal-user').checked;
            $('grant-user-fields').classList.toggle('is-hidden', !isUser);
            $('grant-ou-fields').classList.toggle('is-hidden', isUser);
        });
    });

    document.querySelectorAll('input[name="grant-effect"]').forEach((radio) => {
        radio.addEventListener('change', () => syncGrantEffectUi());
    });
    syncGrantEffectUi();
}

function syncGrantEffectUi() {
    const deny = $('grant-effect-deny')?.checked === true;
    const roleSelect = $('admin-grant-role');
    if (!roleSelect) {
        return;
    }
    roleSelect.disabled = deny;
    roleSelect.classList.toggle('is-hidden', deny);
}

async function loadAdminSoftware() {
    const tbody = $('admin-software-tbody');
    tbody.innerHTML = tableLoadingRow(5, 'Loading software…');
    try {
        const res = await adminListSoftware();
        softwareCache = res.data || [];
        window.adminInstitutionId = res.institutionId;
        tbody.innerHTML = '';
        if (!softwareCache.length) {
            tbody.innerHTML = '<tr><td colspan="5">No software yet.</td></tr>';
            return;
        }
        softwareCache.forEach((sw) => {
            const tr = document.createElement('tr');
            tr.innerHTML = `
                <td>${escapeHtml(sw.name)}</td>
                <td>${sw.url ? `<a href="${escapeHtml(sw.url)}" target="_blank" rel="noopener">${escapeHtml(sw.url)}</a>` : '—'}</td>
                <td>${sw.active === false ? 'Inactive' : 'Active'}</td>
                <td>
                  <label class="admin-inline">
                    <input type="checkbox" data-license="${sw.id}" ${sw.licensed ? 'checked' : ''} />
                    Licensed
                  </label>
                </td>
                <td>
                  <button type="button" class="btn btn-secondary btn-sm" data-edit-roles="${sw.id}">Roles</button>
                  <label class="btn btn-secondary btn-sm admin-file-label">
                    Logo
                    <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" hidden data-logo="${sw.id}" />
                  </label>
                </td>`;
            tbody.appendChild(tr);
        });
        staggerChildren(tbody, 'tr', 35, 0);

        tbody.querySelectorAll('[data-license]').forEach((input) => {
            input.addEventListener('change', async () => {
                try {
                    await adminSetInstitutionAccess(
                        window.adminInstitutionId,
                        input.getAttribute('data-license'),
                        input.checked
                    );
                    showToast('Institution access updated');
                } catch (err) {
                    showError('Could not update license.', err.message);
                    input.checked = !input.checked;
                }
            });
        });

        tbody.querySelectorAll('[data-logo]').forEach((input) => {
            input.addEventListener('change', async () => {
                const file = input.files?.[0];
                if (!file) {
                    return;
                }
                const id = input.getAttribute('data-logo');
                const reader = new FileReader();
                reader.onload = async () => {
                    try {
                        const base64 = String(reader.result).split(',')[1];
                        await adminUploadLogo(id, {
                            contentType: file.type,
                            dataBase64: base64,
                            fileName: file.name
                        });
                        showToast('Logo uploaded');
                        await loadAdminSoftware();
                    } catch (err) {
                        showError('Logo upload failed.', err.message);
                    }
                };
                reader.readAsDataURL(file);
            });
        });

        tbody.querySelectorAll('[data-edit-roles]').forEach((btn) => {
            btn.addEventListener('click', () => {
                const id = btn.getAttribute('data-edit-roles');
                const sw = softwareCache.find((s) => s.id === id);
                if (sw) {
                    openRolesEditor(sw);
                }
            });
        });

        populateAccessSoftwareSelect();
    } catch (err) {
        tbody.innerHTML = `<tr><td colspan="5">${escapeHtml(err.message)}</td></tr>`;
    }
}

function openRolesEditor(software) {
    const roles = (software.roleDefinitions || []).map((r) => ({
        id: r.id || '',
        label: r.label || '',
        rank: Number.isFinite(r.rank) ? r.rank : 0
    }));

    const root = document.createElement('div');
    root.className = 'roles-editor';

    const table = document.createElement('table');
    table.className = 'roles-editor-table';
    table.innerHTML = `
      <thead>
        <tr>
          <th>ID</th>
          <th>Label</th>
          <th>Rank</th>
          <th></th>
        </tr>
      </thead>`;
    const tbody = document.createElement('tbody');
    table.appendChild(tbody);
    root.appendChild(table);

    const addRowBtn = document.createElement('button');
    addRowBtn.type = 'button';
    addRowBtn.className = 'btn btn-secondary btn-sm';
    addRowBtn.textContent = 'Add role';

    const actions = document.createElement('div');
    actions.className = 'roles-editor-actions';
    actions.appendChild(addRowBtn);
    root.appendChild(actions);

    const hint = document.createElement('p');
    hint.className = 'roles-editor-hint';
    hint.textContent = 'OU grants cascade to child OUs; longest path wins. Deny on a child blocks inherited access. Higher rank wins when allow grants tie. IDs are stable strings used by WebAuth (e.g. viewer, badge_admin).';
    root.appendChild(hint);

    const renderRows = () => {
        tbody.innerHTML = '';
        roles.forEach((role, index) => {
            const tr = document.createElement('tr');

            const idInput = document.createElement('input');
            idInput.className = 'field-input';
            idInput.value = role.id;
            idInput.placeholder = 'viewer';
            idInput.addEventListener('input', () => {
                roles[index].id = idInput.value.trim();
            });

            const labelInput = document.createElement('input');
            labelInput.className = 'field-input';
            labelInput.value = role.label;
            labelInput.placeholder = 'Viewer';
            labelInput.addEventListener('input', () => {
                roles[index].label = labelInput.value.trim();
            });

            const rankInput = document.createElement('input');
            rankInput.className = 'field-input';
            rankInput.type = 'number';
            rankInput.value = String(role.rank);
            rankInput.addEventListener('input', () => {
                roles[index].rank = Number(rankInput.value) || 0;
            });

            const removeBtn = document.createElement('button');
            removeBtn.type = 'button';
            removeBtn.className = 'btn btn-danger btn-sm';
            removeBtn.textContent = 'Remove';
            removeBtn.addEventListener('click', () => {
                roles.splice(index, 1);
                renderRows();
            });

            [idInput, labelInput, rankInput, removeBtn].forEach((el) => {
                const td = document.createElement('td');
                td.appendChild(el);
                tr.appendChild(td);
            });
            tbody.appendChild(tr);
        });

        if (!roles.length) {
            const tr = document.createElement('tr');
            tr.innerHTML = '<td colspan="4" class="text-muted">No roles yet — add one below.</td>';
            tbody.appendChild(tr);
        }
    };

    addRowBtn.addEventListener('click', () => {
        roles.push({ id: '', label: '', rank: (roles.length + 1) * 10 });
        renderRows();
    });

    renderRows();

    const modal = new Modal({
        title: `Roles — ${software.name}`,
        content: root,
        actions: [
            {
                label: 'Cancel',
                color: '#6b7280',
                action: () => modal.close()
            },
            {
                label: 'Save roles',
                color: '#00644c',
                animateWhileRunning: true,
                action: async () => {
                    const roleDefinitions = roles
                        .map((r) => ({
                            id: String(r.id || '').trim(),
                            label: String(r.label || '').trim() || String(r.id || '').trim(),
                            rank: Number(r.rank) || 0
                        }))
                        .filter((r) => r.id);

                    const ids = roleDefinitions.map((r) => r.id);
                    if (new Set(ids).size !== ids.length) {
                        showError('Duplicate role IDs', 'Each role ID must be unique.');
                        return;
                    }
                    if (!roleDefinitions.length) {
                        showError('No roles', 'Add at least one role before saving.');
                        return;
                    }

                    try {
                        await adminUpdateSoftware(software.id, { roleDefinitions });
                        showToast('Roles updated');
                        modal.close();
                        await loadAdminSoftware();
                    } catch (err) {
                        showError('Could not update roles.', err.message);
                    }
                }
            }
        ]
    });
    modal.open();
}

async function createSoftware() {
    const name = $('new-software-name').value.trim();
    const url = $('new-software-url').value.trim();
    if (!name) {
        showError('Name required.', 'Enter a software name.');
        return;
    }
    try {
        await adminCreateSoftware({ name, url: url || null, active: true });
        $('new-software-name').value = '';
        $('new-software-url').value = '';
        showToast('Software created');
        await loadAdminSoftware();
    } catch (err) {
        showError('Could not create software.', err.message);
    }
}

function populateAccessSoftwareSelect() {
    const select = $('admin-grants-software');
    if (!select) {
        return;
    }
    const current = select.value;
    select.innerHTML = '<option value="">Select software…</option>';
    softwareCache.forEach((sw) => {
        const opt = document.createElement('option');
        opt.value = sw.id;
        opt.textContent = sw.name;
        select.appendChild(opt);
    });
    if (current) {
        select.value = current;
        selectedSoftwareId = current;
    }
    refreshRoleOptions();
}

function refreshRoleOptions() {
    const select = $('admin-grant-role');
    select.innerHTML = '';
    const sw = softwareCache.find((s) => s.id === selectedSoftwareId);
    const roles = sw?.roleDefinitions || [];
    roles.forEach((r) => {
        const opt = document.createElement('option');
        opt.value = r.id;
        opt.textContent = r.label || r.id;
        select.appendChild(opt);
    });
}

async function loadGrants() {
    const tbody = $('admin-grants-tbody');
    if (!selectedSoftwareId) {
        tbody.innerHTML = '<tr><td colspan="6">Select software first.</td></tr>';
        refreshRoleOptions();
        return;
    }
    refreshRoleOptions();
    syncGrantEffectUi();
    tbody.innerHTML = tableLoadingRow(6, 'Loading grants…');
    try {
        const res = await adminListGrants(selectedSoftwareId);
        const grants = res.data || [];
        tbody.innerHTML = '';
        if (!grants.length) {
            tbody.innerHTML = '<tr><td colspan="6">No grants yet.</td></tr>';
            return;
        }
        grants.forEach((g) => {
            const principal = g.principalType === 'user'
                ? g.principalEmail
                : g.orgUnitPath;
            const effect = g.effect === 'deny' ? 'deny' : 'allow';
            const tr = document.createElement('tr');
            tr.innerHTML = `
                <td>${escapeHtml(g.principalType)}</td>
                <td>${escapeHtml(principal || '')}</td>
                <td>${escapeHtml(effect)}</td>
                <td>${escapeHtml(effect === 'deny' ? '—' : (g.role || ''))}</td>
                <td>${escapeHtml(g.note || '')}</td>
                <td><button type="button" class="btn btn-danger btn-sm" data-revoke="${g.id}">Revoke</button></td>`;
            tbody.appendChild(tr);
        });
        staggerChildren(tbody, 'tr', 30, 0);
        tbody.querySelectorAll('[data-revoke]').forEach((btn) => {
            btn.addEventListener('click', async () => {
                const ok = await showConfirm(
                    'Revoke grant?',
                    'This soft-deletes the grant and recomputes affected user roles.',
                    'Revoke'
                );
                if (!ok) {
                    return;
                }
                try {
                    await adminRevokeGrant(btn.getAttribute('data-revoke'));
                    showToast('Grant revoked');
                    await loadGrants();
                } catch (err) {
                    showError('Could not revoke grant.', err.message);
                }
            });
        });
    } catch (err) {
        tbody.innerHTML = `<tr><td colspan="6">${escapeHtml(err.message)}</td></tr>`;
    }
}

async function addGrant() {
    if (!selectedSoftwareId) {
        showError('Select software first.', '');
        return;
    }
    const isUser = $('grant-principal-user').checked;
    const effect = $('grant-effect-deny')?.checked ? 'deny' : 'allow';
    const role = $('admin-grant-role').value;
    const note = $('admin-grant-note').value.trim();
    const body = {
        softwareId: selectedSoftwareId,
        effect,
        principalType: isUser ? 'user' : 'ou',
        note: note || null
    };
    if (effect === 'allow') {
        body.role = role;
    }
    if (isUser) {
        body.principalEmail = $('admin-user-email').value.trim();
    } else {
        body.orgUnitPath = $('admin-ou-path').value.trim();
    }
    try {
        await adminCreateGrant(body);
        showToast(effect === 'deny' ? 'Deny rule created' : 'Grant created');
        await loadGrants();
    } catch (err) {
        showError('Could not create grant.', err.message);
    }
}

async function filterOrgUnits(q) {
    const list = $('admin-ou-tree');
    list.innerHTML = '<div class="inline-loading" role="status"><div class="spinner spinner-sm" aria-hidden="true"></div><p>Loading OUs…</p></div>';
    try {
        const res = await adminListOrgUnits(q);
        orgUnitsCache = res.data || [];
        list.innerHTML = '';
        if (!orgUnitsCache.length) {
            list.innerHTML = '<p class="text-muted" style="padding:0.75rem;margin:0">No org units found. Run Directory sync first.</p>';
            return;
        }
        orgUnitsCache.slice(0, 80).forEach((ou) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'admin-ou-item';
            btn.textContent = ou.orgUnitPath;
            btn.addEventListener('click', () => {
                $('admin-ou-path').value = ou.orgUnitPath;
            });
            list.appendChild(btn);
        });
        staggerChildren(list, '.admin-ou-item', 20, 0);
    } catch (err) {
        console.error(err);
        list.innerHTML = '<p class="text-muted" style="padding:0.75rem;margin:0">Could not load org units.</p>';
    }
}

async function refreshSyncState() {
    try {
        const res = await adminSyncState();
        const el = $('admin-sync-state');
        if (!res.data) {
            el.textContent = 'Never synced';
            return;
        }
        const at = res.data.syncedAt?._seconds
            ? new Date(res.data.syncedAt._seconds * 1000).toLocaleString()
            : 'Unknown';
        el.textContent = `Last sync: ${at} · ${res.data.orgUnits || 0} OUs · ${res.data.directoryUsers || 0} users`;
    } catch (_) {
        $('admin-sync-state').textContent = 'Sync state unavailable';
    }
}

async function runSync() {
    const btn = $('admin-sync-btn');
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    btn.innerHTML = '<span class="btn-spinner" aria-hidden="true"></span> Syncing…';
    try {
        await adminSyncDirectory();
        showToast('Directory sync complete');
        await refreshSyncState();
        await filterOrgUnits('');
    } catch (err) {
        showError('Directory sync failed.', err.message);
    } finally {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        btn.textContent = 'Sync now';
    }
}

async function runPreview() {
    const email = $('admin-preview-email').value.trim();
    if (!email) {
        return;
    }
    try {
        const res = await adminAccessPreview(email);
        const pre = $('admin-preview-result');
        pre.textContent = JSON.stringify(res.data, null, 2);
    } catch (err) {
        showError('Preview failed.', err.message);
    }
}

async function loadAudit() {
    const tbody = $('admin-audit-tbody');
    tbody.innerHTML = tableLoadingRow(4, 'Loading audit log…');
    try {
        const res = await adminAuditLogs();
        tbody.innerHTML = '';
        (res.data || []).forEach((log) => {
            const when = log.at?._seconds
                ? new Date(log.at._seconds * 1000).toLocaleString()
                : '';
            const tr = document.createElement('tr');
            tr.innerHTML = `
                <td>${escapeHtml(when)}</td>
                <td>${escapeHtml(log.actorEmail || log.actorUid || '')}</td>
                <td>${escapeHtml(log.action)}</td>
                <td>${escapeHtml(log.entityType)} ${escapeHtml(log.entityId || '')}</td>`;
            tbody.appendChild(tr);
        });
        staggerChildren(tbody, 'tr', 30, 0);
    } catch (err) {
        tbody.innerHTML = `<tr><td colspan="4">${escapeHtml(err.message)}</td></tr>`;
    }
}

function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function debounce(fn, ms) {
    let t;
    return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...args), ms);
    };
}
